import {
  type CreateDiscountRequest,
  type Discount,
  DiscountModifierType,
  DiscountTypeDiscriminator,
  type FlatDiscount,
} from '@mynt/backoffice-api-spec/discounts'
import { type CustomerPlanDiscount, DiscountModifier, DiscountType } from './api'

export const mapCreateFlatAndPerUnitDiscountToCreateDiscountRequest = (
  flatAndPerUnitDiscount: CustomerPlanDiscount,
): CreateDiscountRequest => {
  switch (flatAndPerUnitDiscount.discount.modifier) {
    case DiscountModifier.PERCENTAGE: {
      return {
        discountType: {
          type: 'PerUnitAndFlatDiscount',
          modifierType: 'percentage',
          percentageValue: flatAndPerUnitDiscount.discount.percentage,
        },
        expirationDate: flatAndPerUnitDiscount.expirationDate,
      }
    }

    case DiscountModifier.ABSOLUTE: {
      return {
        discountType: {
          type: 'FlatDiscount',
          modifierType: 'absolute',
          absoluteAmount: {
            currency: flatAndPerUnitDiscount.discount.amount.currency,
            value: flatAndPerUnitDiscount.discount.amount.value,
          },
        },
        expirationDate: flatAndPerUnitDiscount.expirationDate,
      }
    }
  }
}

export const mapCustomerDiscountsToPlanDiscount = (discounts: Discount[]): CustomerPlanDiscount | null => {
  const planDiscount = discounts.find(isPlanDiscount)
  return planDiscount ? mapDiscountToFlatAndPerUnitDiscount(planDiscount) : null
}

const isPlanDiscount = (discount: Discount) => {
  return discount.billableId === null
}

type FlatDiscountDto = Discount & {
  discountType: FlatDiscount
}

const isFlatAndPerUnitDiscount = (discount: Discount): discount is FlatDiscountDto => {
  return discount.discountType.type === DiscountTypeDiscriminator.PER_UNIT_AND_FLAT_DISCOUNT
}

export const mapDiscountToFlatAndPerUnitDiscount = (discount: Discount): CustomerPlanDiscount => {
  // For plan discounts, only "flat_and_per_unit" discounts are supported according to Sophie von Corswant
  if (!isFlatAndPerUnitDiscount(discount)) {
    throw new Error(`Unsupported plan discount type: ${discount.discountType.type}`)
  }

  switch (discount.discountType.modifierType) {
    case DiscountModifierType.PERCENTAGE: {
      return {
        type: DiscountType.PER_UNIT_AND_FLAT_DISCOUNT,
        discount: {
          modifier: DiscountModifier.PERCENTAGE,
          percentage: discount.discountType.percentageValue!,
        },
        expirationDate: discount.expirationDate,
      }
    }

    case DiscountModifierType.ABSOLUTE: {
      return {
        type: DiscountType.PER_UNIT_AND_FLAT_DISCOUNT,
        discount: {
          modifier: DiscountModifier.ABSOLUTE,
          amount: discount.discountType.absoluteAmount!,
        },
        expirationDate: discount.expirationDate,
      }
    }
  }
}
